// DE DÓNDE SALE LO QUE SE ADJUNTA: la capa de datos de la biblioteca del Agente,
// compartida por sus DOS superficies — ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes.
//
// Las mismas seis fuentes (productos, modelos, poses, fondos, creaciones y las imágenes
// ya subidas al chat) las muestran el panel lateral de la pantalla dedicada
// (`PanelBiblioteca`, ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente)
// y el selector con pestañas de la caja de la home (`ModalDeFuentes`). Este hook es el
// único lugar que sabe QUÉ se pide, CUÁNDO y cómo se convierte en un adjunto del motor:
// con la carga escrita dos veces, sumar una fuente —o arreglar un vacío— salía bien en
// una superficie y mal en la otra.
//
// Carga PEREZOSA por pestaña y cacheada mientras el hook viva: productos y modelos ya
// vienen del entorno del chat; poses (catálogo + las propias), fondos (los de la marca +
// la biblioteca), creaciones y subidas se piden la primera vez que se abre su pestaña. Un
// pedido en vuelo no se repite si el usuario va y vuelve antes de que llegue
// (`pedidasRef`).
//
// Los adjuntos se arman con `bibliotecaDeAdjuntos.ts` —ids estables— así lo que se
// engancha desde la home se reconoce en el chat y adjuntar dos veces lo mismo no lo
// duplica.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useEntornoChatAtlas } from '../entornoContext';
import {
  listarImagenesSubidasPagina,
  type ChatAttachment,
  type ImagenSubida,
} from '../../pages/v2/agentAttachments';
import {
  cargarGeneradasRecientesPagina,
  type GeneratedPick,
} from '../../pages/v2/GeneratedImagesPicker';
import { fetchAllPoses, type PoseV2 } from '../../pages/v2/posesApi';
import { toRenderThumb } from '../../pages/v2/fondosApi';
import { fetchMyCustomPoses } from '../../pages/v2/posesCustomApi';
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  type FondoBiblioteca,
  type FondoGuardado,
} from '../../pages/v2/fondosApi';
import {
  adjuntoDeFondoDeBiblioteca,
  adjuntoDeFondoGuardado,
  adjuntoDeGenerada,
  adjuntoDeModelo,
  adjuntoDePose,
  adjuntoDeProducto,
  adjuntoDeSubida,
  type PestanaBiblioteca,
} from './bibliotecaDeAdjuntos';

/** Una miniatura de la grilla: lo que se ve y el adjunto que produce al tocarla. */
export interface FichaDeFuente {
  clave: string;
  nombre: string | null;
  imageUrl: string;
  adjunto: ChatAttachment;
  /** Cuándo se usó por última vez. Sólo los productos lo traen: es lo que ordena
   *  "Últimos utilizados" en el selector de fuentes (`fichasDelSelector`). */
  usadoEl?: string | null;
}

interface Poses {
  /** Las de la marca ("Mis poses"): van primero, como en el sheet. */
  propias: PoseV2[];
  catalogo: PoseV2[];
}

interface Fondos {
  guardados: FondoGuardado[];
  biblioteca: FondoBiblioteca[];
}

export interface FuentesDeAdjuntos {
  /** Lo que muestra la pestaña pedida, en el orden en que se ve. */
  fichas: FichaDeFuente[];
  /** Su catálogo todavía no llegó (las fuentes del entorno nunca cargan). */
  cargando: boolean;
  /** Después de subir una pose propia, para que aparezca en la grilla. */
  refrescarPosesPropias: () => Promise<void>;
  /** Después de guardar un fondo de la marca. */
  refrescarFondosGuardados: () => Promise<void>;
  /** Después de subir imágenes al chat: ya son parte de "Subidas". */
  refrescarSubidas: () => Promise<void>;
  /**
   * Creaciones y Subidas se piden por PÁGINAS (`PAGINA_DE_FUENTE` a la vez): `hayMas` dice
   * si la última página vino llena y `pedirMas` trae la siguiente
   * (ADR-2026-09-25-el-selector-de-fuentes-carga-miniaturas-y-por-scroll). El resto de
   * las fuentes llega entera (son los catálogos del entorno): ahí `hayMas` es `false`.
   */
  hayMas: boolean;
  pedirMas: () => void;
  /** Hay una página pedida en vuelo. */
  pidiendoMas: boolean;
}

/** Cuántas creaciones o subidas se piden por vez. Es lo que se pedía antes de una. */
export const PAGINA_DE_FUENTE = 60;

export const useFuentesDeAdjuntos = (pestana: PestanaBiblioteca): FuentesDeAdjuntos => {
  const { productos, modelos, companyId } = useEntornoChatAtlas();
  // `null` = todavía no se pidió; `[]` = se pidió y no hay.
  const [poses, setPoses] = useState<Poses | null>(null);
  const [fondos, setFondos] = useState<Fondos | null>(null);
  const [creaciones, setCreaciones] = useState<GeneratedPick[] | null>(null);
  const [subidas, setSubidas] = useState<ImagenSubida[] | null>(null);
  // Qué pestañas ya se pidieron: un pedido en vuelo no se repite si el usuario va y
  // vuelve antes de que llegue.
  //
  // La clave lleva el `companyId` PEGADO, y no es de más
  // (ADR-2026-09-24-un-pedido-sin-empresa-o-que-falla-se-vuelve-a-pedir): el entorno del
  // chat lo resuelve async, así que abrir "Creaciones" o "Subidas" en el primer segundo
  // pedía sin empresa, guardaba `[]` y —con la pestaña ya marcada como pedida— no volvía
  // a intentar nunca. Con el id adentro de la clave, cuando llega, la clave cambia y se
  // pide de nuevo.
  const pedidasRef = useRef(new Set<string>());
  // Hasta dónde se pidió cada fuente paginada. Pedir más es subir el tope y volver a
  // pedir la lista entera hasta ahí: es lo que hace la columna de resultados
  // (`setLimiteCreaciones`), y las firmas ya pedidas salen de la caché.
  const [limites, setLimites] = useState({ creaciones: PAGINA_DE_FUENTE, subidas: PAGINA_DE_FUENTE });
  // El tope VIGENTE de cada fuente, para descartar la respuesta de una página vieja que
  // llega después de la nueva (sin esto, la de 60 pisaba la de 120 y dejaba afuera lo
  // que ya se había traído) — ADR-2026-09-25-revision-greptile-el-paginado-se-mide-sobre-lo-que-trajo-la-base.
  const limitesRef = useRef(limites);
  limitesRef.current = limites;
  // ¿La última página de cada fuente vino llena? Se mide sobre lo que devolvió la BASE,
  // no sobre lo que quedó después de filtrar (videos en Subidas, filas sin miniatura en
  // Creaciones): con eso, un solo archivo descartado cortaba el paginado.
  const [vinoLlena, setVinoLlena] = useState({ creaciones: false, subidas: false });
  // Una página pedida y todavía en vuelo: el centinela del pie dispara con cada evento de
  // scroll, y sin este freno salían varias páginas encimadas.
  // Uno POR FUENTE: con uno solo, una página de Creaciones en vuelo frenaba el scroll de
  // Subidas, y la respuesta de una liberaba el freno de la otra.
  const [pidiendoMasPor, setPidiendoMasPor] = useState({ creaciones: false, subidas: false });
  // La empresa VIGENTE, para descartar la respuesta de un pedido que salió con otra
  // (ADR-2026-09-24-la-respuesta-de-la-empresa-vieja-no-pisa-a-la-nueva). Con el id en la
  // clave conviven dos pedidos de la misma pestaña —el de "sin empresa" y el bueno— y el
  // viejo puede llegar último: en Poses y en Fondos eso dejaba la pestaña con el catálogo
  // pero SIN lo propio de la marca.
  const empresaRef = useRef(companyId);

  // Carga perezosa por pestaña.
  useEffect(() => {
    empresaRef.current = companyId;
    const limite =
      pestana === 'creaciones' || pestana === 'subidas' ? limites[pestana] : 0;
    const clave = `${pestana}:${companyId ?? 'sin-empresa'}:${limite}`;
    if (pedidasRef.current.has(clave)) return;
    pedidasRef.current.add(clave);
    // Sólo escribe el pedido cuya empresa sigue siendo la de ahora. NO se cancela por
    // cambiar de pestaña: el caché de `pedidasRef` cuenta con que un pedido en vuelo
    // llegue y se aplique aunque el usuario se haya ido y vuelto (si se descartara, la
    // pestaña se quedaría cargando para siempre, porque no se vuelve a pedir).
    const vigente = () =>
      empresaRef.current === companyId &&
      (pestana !== 'creaciones' && pestana !== 'subidas'
        ? true
        : limitesRef.current[pestana] === limite);
    const alLlegar =
      <T,>(aplicar: (v: T) => void) =>
      (v: T): void => {
        if (vigente()) aplicar(v);
      };
    // Un pedido que FALLA se borra de la lista: si no, un error de red de un segundo
    // dejaba la pestaña vacía por el resto de la sesión, y volver a abrirla no hacía
    // nada. Borrada la clave, el próximo `useEffect` (cambiar de pestaña y volver)
    // reintenta.
    const reintentable = (caer: () => void) => (): void => {
      pedidasRef.current.delete(clave);
      if (vigente()) caer();
    };
    if (pestana === 'poses') {
      Promise.all([
        fetchAllPoses(),
        companyId ? fetchMyCustomPoses(companyId) : Promise.resolve([] as PoseV2[]),
      ])
        .then(alLlegar(([catalogo, propias]) => setPoses({ propias, catalogo })))
        .catch(reintentable(() => setPoses({ propias: [], catalogo: [] })));
    } else if (pestana === 'fondos') {
      Promise.all([
        companyId ? fetchFondosGuardados(companyId) : Promise.resolve([] as FondoGuardado[]),
        fetchBibliotecaFondos(null),
      ])
        .then(alLlegar(([guardados, biblioteca]) => setFondos({ guardados, biblioteca })))
        .catch(reintentable(() => setFondos({ guardados: [], biblioteca: [] })));
    } else if (pestana === 'creaciones') {
      if (!companyId) setCreaciones([]);
      else
        // Con los videos: un video de Creaciones se adjunta como video
        // (ADR-2026-09-25-un-video-de-creaciones-se-adjunta-como-video).
        cargarGeneradasRecientesPagina(companyId, limite, { incluirVideos: true })
          .then(
            alLlegar(({ picks, vinoLlena: llena }) => {
              setCreaciones(picks);
              setVinoLlena((v) => ({ ...v, creaciones: llena }));
              setPidiendoMasPor((p) => ({ ...p, creaciones: false }));
            }),
          )
          .catch(
            reintentable(() => {
              setCreaciones((prev) => prev ?? []);
              setPidiendoMasPor((p) => ({ ...p, creaciones: false }));
            }),
          );
    } else if (pestana === 'subidas') {
      if (!companyId) setSubidas([]);
      else
        listarImagenesSubidasPagina(companyId, limite)
          .then(
            alLlegar(({ imagenes, vinoLlena: llena }) => {
              setSubidas(imagenes);
              setVinoLlena((v) => ({ ...v, subidas: llena }));
              setPidiendoMasPor((p) => ({ ...p, subidas: false }));
            }),
          )
          .catch(
            reintentable(() => {
              setSubidas((prev) => prev ?? []);
              setPidiendoMasPor((p) => ({ ...p, subidas: false }));
            }),
          );
    }
  }, [pestana, companyId, limites]);

  const refrescarPosesPropias = async () => {
    if (!companyId) return;
    const propias = await fetchMyCustomPoses(companyId);
    setPoses((prev) => ({ propias, catalogo: prev?.catalogo ?? [] }));
  };
  const refrescarFondosGuardados = async () => {
    if (!companyId) return;
    const guardados = await fetchFondosGuardados(companyId);
    setFondos((prev) => ({ guardados, biblioteca: prev?.biblioteca ?? [] }));
  };
  const refrescarSubidas = async () => {
    if (!companyId) return;
    const { imagenes, vinoLlena: llena } = await listarImagenesSubidasPagina(
      companyId,
      limites.subidas,
    );
    setSubidas(imagenes);
    setVinoLlena((v) => ({ ...v, subidas: llena }));
  };
  // ¿Puede haber más? Si la última página vino llena. Cuando el total es justo un múltiplo
  // de la página, eso cuesta UN pedido de más: vuelve con menos del tope y ahí corta.
  const hayMas =
    (pestana === 'creaciones' && vinoLlena.creaciones) ||
    (pestana === 'subidas' && vinoLlena.subidas);
  const pidiendoMas =
    (pestana === 'creaciones' && pidiendoMasPor.creaciones) ||
    (pestana === 'subidas' && pidiendoMasPor.subidas);
  const pedirMas = () => {
    if (!hayMas || pidiendoMas) return;
    const fuente = pestana as 'creaciones' | 'subidas';
    setPidiendoMasPor((p) => ({ ...p, [fuente]: true }));
    setLimites((l) => ({ ...l, [fuente]: l[fuente] + PAGINA_DE_FUENTE }));
  };

  const fichas = useMemo<FichaDeFuente[]>(() => {
    switch (pestana) {
      case 'productos':
        return productos.map((p) => ({
          clave: `producto-${p.id}`,
          nombre: p.name,
          imageUrl: p.imageUrl,
          adjunto: adjuntoDeProducto(p),
          usadoEl: p.lastUsedAt ?? null,
        }));
      case 'modelos':
        return modelos.map((m) => ({
          clave: `modelo-${m.id}`,
          nombre: m.nombre,
          // `thumbUrl` (la miniatura transformada) y no `thumbnailUrl`, que a pesar del
          // nombre es la foto en resolución completa: las de presentación son PNGs
          // grandes y decodificar todas de una reventaba el compositor (ver `modelsApi`).
          // Es lo que ya hace el picker de modelos.
          imageUrl: m.thumbUrl,
          adjunto: adjuntoDeModelo(m),
        }));
      case 'poses':
        return [...(poses?.propias ?? []), ...(poses?.catalogo ?? [])]
          .filter((p) => !!p.imageUrl)
          .map((p) => ({
            clave: `pose-${p.id}`,
            nombre: p.nombre,
            // Sólo lo que se DIBUJA es miniatura; el adjunto se arma con `p` tal cual, o
            // sea con el original (ADR-2026-09-25-revision-greptile-la-miniatura-no-viaja-en-el-adjunto).
            // Las propias traen su miniatura firmada (`thumbUrl`); las del catálogo son
            // JPEG públicos de hasta ~900 KB y se piden por `render/image`, como los
            // fondos de biblioteca.
            imageUrl: p.thumbUrl ?? toRenderThumb(p.imageUrl),
            adjunto: adjuntoDePose(p),
          }));
      case 'fondos':
        // Primero los de la marca (son pocos y son suyos), después la biblioteca.
        return [
          ...(fondos?.guardados ?? []).map((g) => ({
            clave: `fondo-guardado-${g.fileName}`,
            nombre: g.nombre,
            imageUrl: g.imageUrl,
            adjunto: adjuntoDeFondoGuardado(g),
          })),
          ...(fondos?.biblioteca ?? []).map((b) => ({
            clave: `fondo-biblioteca-${b.id}`,
            nombre: b.nombre,
            imageUrl: b.imageUrl,
            adjunto: adjuntoDeFondoDeBiblioteca(b),
          })),
        ];
      case 'creaciones':
        return (creaciones ?? []).map((g) => ({
          clave: `creacion-${g.id}`,
          nombre: null,
          imageUrl: g.previewUrl,
          adjunto: adjuntoDeGenerada(g),
        }));
      case 'subidas':
        return (subidas ?? []).map((s) => ({
          clave: `subida-${s.storagePath}`,
          nombre: null,
          // La miniatura para la grilla; el adjunto lleva el original (`previewUrl`).
          imageUrl: s.miniaturaUrl ?? s.previewUrl,
          adjunto: adjuntoDeSubida(s),
        }));
      case 'moodboards':
        // Sus fichas las arma `BibliotecaMoodboards`: tiene dos niveles (carpetas y
        // fotos) y sus propias altas. `ModalDeFuentes` arma los suyos, también aparte
        // (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes).
        return [];
      case 'elementos':
        // También dos niveles: los arma `ModalDeFuentes` (ADR-2026-10-19-elementos-de-marca).
        return [];
    }
  }, [pestana, productos, modelos, poses, fondos, creaciones, subidas]);

  const cargando =
    (pestana === 'poses' && poses === null) ||
    (pestana === 'fondos' && fondos === null) ||
    (pestana === 'creaciones' && creaciones === null) ||
    (pestana === 'subidas' && subidas === null);

  return {
    fichas,
    cargando,
    refrescarPosesPropias,
    refrescarFondosGuardados,
    refrescarSubidas,
    hayMas,
    pedirMas,
    pidiendoMas,
  };
};
